import logger from "@/lib/logger";
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, Printer, Trash2, Edit3, CheckCircle, Loader2, User, Package, PackageOpen, Wrench, CreditCard, AlertCircle, Plus, Zap, Truck, ExternalLink, RotateCcw, Gift, Info } from "lucide-react";
import { LEGACY_PIPELINE, ALL_STATUSES, StatusBadge, getStep, getLegacyPipelineIdx, POST_RECEIVED_PIPELINE, POST_RX_HISTORY, isPostReceived, getPostReceivedIdx, fmtPostReceived } from "@/components/StatusPipeline";
import BarcodePanel from "@/components/BarcodePanel";
import { CHARGER_SPECS, MONTHS, YEARS, BODY_TYPES } from "@/pages/NewJob";
import { INDIA_STATES } from "@/lib/constants";
import { COURIER_SERVICES, buildTrackingUrl } from "@/lib/courier";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Inbound (Customer Sending) pipeline ──────────────────────────────────────
const INBOUND_PIPELINE = [
  { status:"Courier Initiated",         icon:Package,     emoji:"📦", dot:"#7c3aed", desc:"Customer has initiated a courier to send the charger" },
  { status:"In Transit",                icon:Truck,       emoji:"🚚", dot:"#2563eb", desc:"Charger is in transit to service center" },
  { status:"Received at Service Center",icon:PackageOpen, emoji:"🏪", dot:"#16a34a", desc:"Charger has arrived at the service center" },
];

// ── Charger segment config (mirrors ServiceEntry.jsx) ─────────────────────
const CHARGER_SEGMENTS = [
  { label: "Small Charger",    icon: "🔋" },
  { label: "Fast Charger",     icon: "⚡" },
  { label: "Rickshaw Charger", icon: "🛺" },
];
const SEGMENT_MODELS = {
  "Small Charger":    ["54.6V / 6A","58.4V / 6A","67.2V / 6A","69.3V / 6A","71.2V / 6A","73V / 6A","84V / 5A"],
  "Fast Charger":     ["54.6V / 10A","58.4V / 10A","67.2V / 10A","68.4V / 10A","69.3V / 10A","71.2V / 10A","73V / 10A","84V / 10A"],
  "Rickshaw Charger": ["54.6V / 15A","58.4V / 15A","67.2V / 15A","69.3V / 15A","71.2V / 15A","73V / 15A","54.6V / 20A","58.4V / 20A","67.2V / 20A","69.3V / 20A","71.2V / 20A","73V / 20A"],
};
const isEligibleForNew = (month, year) => {
  if (!month || !year) return false;
  const m = parseInt(month) - 1;
  if (isNaN(m)) return false;
  const purchase = new Date(parseInt(year), m, 1);
  const cutoff = new Date(); cutoff.setMonth(cutoff.getMonth() - 3);
  return purchase >= cutoff;
};
const EMPTY_COURIER = { courier_name:"", awb_number:"", tracking_url:"", dispatch_date:"", expected_return_date:"" };

const PAY_STYLES = {
  "Unpaid":  "bg-rose-50 text-rose-700 border-rose-200",
  "Partial": "bg-amber-50 text-amber-700 border-amber-200",
  "Paid":    "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "";
const fmtDT   = d => d ? new Date(d).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "";

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate  = useNavigate();
  const appUrl    = window.location.origin;

  const [job,     setJob]    = useState(null);
  const [loading, setLoading]= useState(true);
  const [error,   setError]  = useState(null);
  const [saving,  setSaving] = useState(false);

  const [editCustomer, setEditCustomer]= useState(false);
  const [editProduct,  setEditProduct] = useState(false);
  const [editSpecs,    setEditSpecs]   = useState(false);
  const [editPayment,  setEditPayment] = useState(false);
  const [updateStatus, setUpdateStatus]= useState(false);
  const [deleteConfirm,setDeleteConfirm]=useState(false);
  const [editCourier,  setEditCourier] = useState(false);
  const [assignTech,   setAssignTech]  = useState(false);
  const [technicians,  setTechnicians] = useState([]);

  const [custForm,  setCustForm]  = useState({});
  const [prodForm,  setProdForm]  = useState({
    charger_type:"", charger_spec:"", serial_number:"", part_number:"", body_type:"",
    purchase_month:"", purchase_year:"",
    xp_charger_status:"",
    outgoing_courier:{ ...EMPTY_COURIER },
    incoming_courier:{ ...EMPTY_COURIER },
  });
  const [prodEditIdx, setProdEditIdx] = useState(null); // null = add new
  const [specsForm, setSpecsForm] = useState({});
  const [payForm,   setPayForm]   = useState({});
  const [statusForm,setStatusForm]= useState({ status: "", notes: "" });
  const [courierForm, setCourierForm] = useState({ courier_name:"", awb_number:"", tracking_url:"" });

  // ── Inbound (Customer Sending) mode ──────────────────────────────────────
  const [inboundCourierForm, setInboundCourierForm] = useState({ courier_name:"", awb_number:"", tracking_url:"" });
  const [editInbound, setEditInbound] = useState(false);
  const [savingMode,  setSavingMode]  = useState(false);

  useEffect(() => {
    const loadJob = async () => {
      try {
        setLoading(true);
        const r = await api.get(`${API}/jobs/${jobId}`);
        setJob(r.data);
      } catch (err) {
        logger.error("[JobDetail] load failed:", err?.message);
        setError("Job not found or failed to load");
      } finally { setLoading(false); }
    };
    loadJob();
    api.get(`${API}/technicians`).then(r => setTechnicians(r.data.technicians || [])).catch(() => {});
  }, [jobId]); // jobId is the only true external dep; api/API are stable module refs

  const fetchJob = async () => {
    try { setLoading(true); const r = await api.get(`${API}/jobs/${jobId}`); setJob(r.data); }
    catch (err) { logger.error("[JobDetail] fetchJob:", err?.message); setError("Job not found or failed to load"); }
    finally { setLoading(false); }
  };

  const save = async (field, data) => {
    setSaving(true);
    try { const r = await api.put(`${API}/jobs/${job.id}`, { [field]: data }); setJob(r.data); toast.success("Updated!"); }
    catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const saveProduct = async (productData) => {
    const data = productData || prodForm;
    setSaving(true);
    try {
      const current = job.products || [];
      const updated = prodEditIdx === null
        ? [...current, { ...data }]
        // Merge with existing product so photo_url and other fields not in the form are preserved
        : current.map((p, i) => i === prodEditIdx ? { ...p, ...data } : p);
      const r = await api.put(`${API}/jobs/${job.id}`, { products: updated });
      setJob(r.data);
      toast.success(prodEditIdx === null ? "Charger added!" : "Charger updated!");
      setEditProduct(false);
    } catch { toast.error("Failed to save charger"); }
    finally { setSaving(false); }
  };

  const deleteProductAtIdx = async (idx) => {
    const updated = (job.products || []).filter((_, i) => i !== idx);
    try {
      const r = await api.put(`${API}/jobs/${job.id}`, { products: updated });
      setJob(r.data);
      toast.success("Charger removed");
    } catch { toast.error("Failed to remove charger"); }
  };

  const saveCourier = async () => {
    setSaving(true);
    try {
      const trackUrl = courierForm.tracking_url ||
        buildTrackingUrl(courierForm.courier_name, courierForm.awb_number);
      const r = await api.put(`${API}/jobs/${job.id}/courier`, {
        ...courierForm,
        tracking_url: trackUrl,
      });
      setJob(r.data);
      toast.success("Courier details saved!");
      setEditCourier(false);
    } catch { toast.error("Failed to save courier details"); }
    finally { setSaving(false); }
  };

  // ── Inbound mode handlers ─────────────────────────────────────────────────
  const handleSetMode = async (mode) => {
    setSavingMode(true);
    try {
      const payload = { service_mode: mode };
      if (mode === "customer_sending" && !job.inbound_status) {
        payload.inbound_status = "Courier Initiated";
      }
      const r = await api.put(`${API}/jobs/${job.id}`, payload);
      setJob(r.data);
      toast.success(mode === "customer_sending" ? "Switched to Customer Sending mode" : "Switched to Standard Timeline");
    } catch { toast.error("Failed to switch mode"); }
    finally { setSavingMode(false); }
  };

  const handleSaveInboundCourier = async () => {
    setSaving(true);
    try {
      const r = await api.put(`${API}/jobs/${job.id}`, { inbound_courier: inboundCourierForm });
      setJob(r.data);
      setEditInbound(false);
      toast.success("Inbound courier details saved");
    } catch { toast.error("Failed to save inbound courier"); }
    finally { setSaving(false); }
  };

  const handleAdvanceInboundStatus = async () => {
    const steps = INBOUND_PIPELINE.map(s => s.status);
    const curr  = steps.indexOf(job.inbound_status);
    if (curr >= steps.length - 1) return;
    const next = steps[curr + 1];
    setSaving(true);
    try {
      const r = await api.put(`${API}/jobs/${job.id}`, { inbound_status: next });
      setJob(r.data);
      toast.success(`Inbound status → ${next}`);
    } catch { toast.error("Failed to update inbound status"); }
    finally { setSaving(false); }
  };

  const saveStatus = async () => {
    setSaving(true);
    try { const r = await api.put(`${API}/jobs/${job.id}/status`, statusForm); setJob(r.data); toast.success(`Status → ${statusForm.status}`); setUpdateStatus(false); }
    catch { toast.error("Failed to update status"); }
    finally { setSaving(false); }
  };

  const deleteJob = async () => {
    try { await api.delete(`${API}/jobs/${job.id}`); toast.success("Deleted"); navigate("/jobs"); }
    catch { toast.error("Delete failed"); }
  };

  const Spin = () => saving ? <Loader2 size={13} className="mr-1 animate-spin" /> : null;

  if (loading) return <div className="px-6 py-6"><Skeleton className="h-8 w-64 mb-4" />{[...Array(3)].map((_,i)=><Skeleton key={i} className="h-40 mb-4 rounded-2xl"/>)}</div>;
  if (error)   return <div className="px-6 py-6"><div className="flex items-center gap-2 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700"><AlertCircle size={16}/>{error}</div></div>;

  // Map status → LEGACY_PIPELINE index (same 9 steps as tracking page)
  const STATUS_TO_STEP = {
    "Under Inspection":"Received","Waiting for Approval":"Received",
    "Repair in Progress":"Repairing","Repaired":"Repairing",
    "Replacement Approved":"In Dispatch","Replacement Dispatched":"In Dispatch",
    "Out for Delivery":"In Dispatch","Completed":"In Transit",
    "Warranty Rejected":"In Transit","In Repair":"Repairing",
    "Delivered":"In Transit",  // backward compat for old jobs
  };
  const getLegacyIdx = (status) => {
    const mapped = STATUS_TO_STEP[status];
    const resolvedStatus = mapped || status;
    return getLegacyPipelineIdx(resolvedStatus, job.status_history || []);
  };
  const currentIdx  = getLegacyIdx(job.current_status);
  const trackingUrl = `${appUrl}/track?job=${job.job_no}`;

  // Build history map for timeline
  const historyMap = {};
  (job.status_history || []).forEach(h => { historyMap[h.status] = h; });

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/jobs")}><ArrowLeft size={16}/></Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xl font-bold text-green-700">{job.job_no}</span>
              <StatusBadge status={job.current_status} />
              {job.technician && (
                <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-0.5 rounded-full font-medium">
                  👷 {job.technician}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Created {fmtDate(job.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-11 sm:ml-0 flex-wrap">
          <a href={trackingUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="h-8 text-xs border-green-200 text-green-700 hover:bg-green-50">Customer Link</Button>
          </a>
          <a href={`/print/${job.id}`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="invoice-print-button"><Printer size={14} className="mr-1.5"/>Print</Button>
          </a>
          <Button size="sm" className="h-8 bg-green-600 hover:bg-green-700 text-white text-xs"
            onClick={() => { setStatusForm({ status: job.current_status, notes: "" }); setUpdateStatus(true); }}
            data-testid="job-detail-update-status-button">
            <CheckCircle size={14} className="mr-1.5"/>Update Status
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8 text-rose-500 hover:bg-rose-50 border-rose-200" onClick={() => setDeleteConfirm(true)}><Trash2 size={14}/></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT — main info */}
        <div className="lg:col-span-2 space-y-5">

          {/* Barcode */}
          <BarcodePanel jobNo={job.job_no} trackingUrl={trackingUrl} />

          {/* Customer */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-green-50 flex items-center justify-center"><User size={15} className="text-green-600"/></div>
                  <h3 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Customer Details</h3>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-green-600" onClick={() => { setCustForm({...job.customer}); setEditCustomer(true); }}><Edit3 size={13}/></Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  ["Name",       job.customer?.name,     false],
                  ["Phone",      job.customer?.phone,    false],
                  ["Alt. Phone", job.customer?.alt_phone,false],
                  ["City",       job.customer?.city,     false],
                  ["State",      job.customer?.state,    false],
                  ["PIN Code",   job.customer?.pincode,  true],
                ].map(([l,v,m])=>(
                  <div key={l}><p className="text-xs text-slate-400 mb-0.5">{l}</p>
                    <p className={`text-sm font-medium text-slate-900 ${m?"font-mono":""}`}>{v||"—"}</p>
                  </div>
                ))}
                {job.customer?.address && (
                  <div className="col-span-2 sm:col-span-3"><p className="text-xs text-slate-400 mb-0.5">Address</p>
                    <p className="text-sm text-slate-700">{job.customer.address}{job.customer?.landmark ? ` · Near: ${job.customer.landmark}` : ""}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ===== CHARGER TABLE ===== */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-green-600 flex items-center justify-center"><Zap size={15} className="text-white"/></div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Charger Details</h3>
                    <p className="text-xs text-slate-400">{(job.products||[]).length} charger{(job.products||[]).length!==1?"s":""}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="h-7 text-xs border-green-200 text-green-700 hover:bg-green-50"
                  onClick={() => { setEditProduct(true); setProdEditIdx(null); setProdForm({charger_type:"",charger_spec:"",serial_number:"",body_type:"",purchase_month:"",purchase_year:"",xp_charger_status:"",outgoing_courier:{...EMPTY_COURIER},incoming_courier:{...EMPTY_COURIER}}); }}>
                  <Plus size={13} className="mr-1"/>Add Charger
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      {["#","Type","Model","Serial No.","Purchase","Body Type","XP Status",""].map(h=>(
                        <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-3 py-2 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(job.products||[]).length === 0 ? (
                      <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-slate-400 italic">No chargers added yet</td></tr>
                    ) : (job.products||[]).map((p, i) => (
                      <>
                        <tr key={p.serial_number || p.charger_spec + i} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                          <td className="px-3 py-2.5">
                            <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold inline-flex items-center justify-center">{i+1}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            {p.charger_type ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium whitespace-nowrap">{p.charger_type}</span>
                            ) : <span className="text-xs text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="font-mono font-semibold text-green-700 text-sm">{p.charger_spec||"—"}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="text-sm font-mono text-slate-700">{p.serial_number||"—"}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="text-sm text-slate-700">{p.purchase_date||"—"}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            {p.body_type ? (
                              <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                                p.body_type==="Metal Body" ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-sky-50 text-sky-700 border-sky-200"
                              }`}>{p.body_type}</span>
                            ) : <span className="text-xs text-slate-400">—</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            {p.xp_charger_status === "returned" && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 whitespace-nowrap">🔄 Returned</span>
                            )}
                            {p.xp_charger_status === "new_given" && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 whitespace-nowrap">🎁 New Given</span>
                            )}
                            {!p.xp_charger_status && <span className="text-xs text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <div className="flex items-center gap-1 justify-end">
                              <button className="p-1 text-slate-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                      onClick={() => {
                        const pm = (p.purchase_date||"").split(" ")[0]||"";
                        const py = (p.purchase_date||"").split(" ")[1]||"";
                        setProdForm({
                          charger_type: p.charger_type||"",
                          charger_spec: p.charger_spec||"",
                          serial_number: p.serial_number||"",
                          body_type: p.body_type||"",
                          purchase_month: pm,
                          purchase_year: py,
                          xp_charger_status: p.xp_charger_status||"",
                          outgoing_courier: p.outgoing_courier || { ...EMPTY_COURIER },
                          incoming_courier: p.incoming_courier || { ...EMPTY_COURIER },
                        });
                        setProdEditIdx(i); setEditProduct(true);
                      }}>
                                <Edit3 size={12}/>
                              </button>
                              {(job.products||[]).length > 1 && (
                                <button className="p-1 text-slate-400 hover:text-rose-500 rounded-lg hover:bg-rose-50" onClick={() => deleteProductAtIdx(i)}>
                                  <Trash2 size={12}/>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Courier rows for XP chargers */}
                        {p.xp_charger_status && (p.outgoing_courier?.awb_number || p.incoming_courier?.awb_number) && (
                          <tr key={"courier-" + i} className="border-b border-slate-50">
                            <td colSpan={8} className="px-3 py-2 bg-slate-50/60">
                              <div className="flex flex-wrap gap-3">
                                {p.outgoing_courier?.awb_number && (
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="text-[9px] font-bold text-green-700 uppercase bg-green-100 px-1.5 py-0.5 rounded">→ Outgoing</span>
                                    <span className="text-slate-600 font-medium">{p.outgoing_courier.courier_name||""}</span>
                                    <span className="font-mono font-bold text-green-700">{p.outgoing_courier.awb_number}</span>
                                    {p.outgoing_courier.tracking_url && (
                                      <a href={p.outgoing_courier.tracking_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[10px]">Track ↗</a>
                                    )}
                                  </div>
                                )}
                                {p.incoming_courier?.awb_number && (
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="text-[9px] font-bold text-orange-700 uppercase bg-orange-100 px-1.5 py-0.5 rounded">← Incoming</span>
                                    <span className="text-slate-600 font-medium">{p.incoming_courier.courier_name||""}</span>
                                    <span className="font-mono font-bold text-orange-700">{p.incoming_courier.awb_number}</span>
                                    {p.incoming_courier.tracking_url && (
                                      <a href={p.incoming_courier.tracking_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[10px]">Track ↗</a>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        {/* Photo row — shown only when photo_url is set for this charger */}
                        {p.photo_url && (
                          <tr key={"photo-" + i} className="border-b border-slate-50">
                            <td colSpan={8} className="px-3 py-2.5 bg-slate-50/30">
                              <div className="flex items-center gap-3">
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Charger Photo</span>
                                <a
                                  href={`${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Click to view full size"
                                >
                                  <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`}
                                    alt={"Charger " + (i+1) + " photo"}
                                    className="w-20 h-20 object-cover rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-green-300 transition-all"
                                  />
                                </a>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Fault & Notes */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center"><Wrench size={15} className="text-amber-600"/></div>
                  <h3 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Fault & Notes</h3>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-green-600" onClick={() => { setSpecsForm({...(job.specs||{})}); setEditSpecs(true); }}><Edit3 size={13}/></Button>
              </div>
              <div className="space-y-3">
                <div><p className="text-xs text-slate-400 mb-1">Fault Description</p><p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg min-h-[40px]">{job.specs?.fault_description||<span className="text-slate-400 italic">Not specified</span>}</p></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-slate-400 mb-1">Technical Notes</p><p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg min-h-[40px]">{job.specs?.technical_notes||<span className="text-slate-400 italic">None</span>}</p></div>
                  <div><p className="text-xs text-slate-400 mb-1">Repair Notes</p><p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg min-h-[40px]">{job.specs?.repair_notes||<span className="text-slate-400 italic">None</span>}</p></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== SERVICE JOURNEY / INBOUND TIMELINE ===== */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              {/* ── Mode selector ── */}
              <div className="flex gap-2 mb-4">
                <button type="button"
                  disabled={savingMode}
                  onClick={() => handleSetMode("")}
                  className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-semibold border-2 transition-all ${
                    !job.service_mode || job.service_mode === ""
                      ? "bg-green-600 text-white border-green-600"
                      : "bg-white text-slate-600 border-slate-200 hover:border-green-400 hover:text-green-700"
                  }`}>
                  🚗 Standard Timeline
                </button>
                <button type="button"
                  disabled={savingMode}
                  onClick={() => handleSetMode("customer_sending")}
                  className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-semibold border-2 transition-all ${
                    job.service_mode === "customer_sending"
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-white text-slate-600 border-slate-200 hover:border-orange-400 hover:text-orange-700"
                  }`}>
                  📦 Customer Sending Charger
                </button>
              </div>

              {job.service_mode === "customer_sending" ? (
                /* ── INBOUND TIMELINE ── */
                <div>
                  {/* Inbound status banner */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>
                      Inbound Shipment Tracking
                    </h3>
                    <span className="text-xs text-slate-400">
                      {INBOUND_PIPELINE.findIndex(s => s.status === job.inbound_status) + 1} / {INBOUND_PIPELINE.length} steps
                    </span>
                  </div>

                  {/* Inbound progress bar */}
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-5">
                    <div className="h-full bg-orange-500 rounded-full transition-all"
                      style={{ width: `${((INBOUND_PIPELINE.findIndex(s => s.status === job.inbound_status) + 1) / INBOUND_PIPELINE.length) * 100}%` }} />
                  </div>

                  {/* Inbound courier details card */}
                  <div className={`mb-5 p-4 rounded-xl border ${job.inbound_courier?.awb_number ? "bg-orange-50 border-orange-200" : "bg-slate-50 border-slate-200"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                        <Truck size={12} className="text-orange-600" /> Customer's Inbound Courier
                      </p>
                      <button type="button"
                        onClick={() => { setInboundCourierForm({ courier_name: job.inbound_courier?.courier_name||"", awb_number: job.inbound_courier?.awb_number||"", tracking_url: job.inbound_courier?.tracking_url||"" }); setEditInbound(true); }}
                        className="text-xs text-green-700 hover:text-green-800 flex items-center gap-1 font-medium">
                        <Edit3 size={11}/> {job.inbound_courier?.awb_number ? "Edit" : "Add Details"}
                      </button>
                    </div>
                    {job.inbound_courier?.awb_number ? (
                      <div className="space-y-1">
                        {job.inbound_courier.courier_name && (
                          <div className="flex justify-between text-xs"><span className="text-slate-500">Courier</span><span className="font-semibold text-slate-800">{job.inbound_courier.courier_name}</span></div>
                        )}
                        <div className="flex justify-between text-xs"><span className="text-slate-500">AWB Number</span><span className="font-mono font-bold text-orange-700">{job.inbound_courier.awb_number}</span></div>
                        {job.inbound_courier.tracking_url && (
                          <a href={job.inbound_courier.tracking_url} target="_blank" rel="noreferrer"
                            className="flex items-center gap-1 text-xs text-blue-600 hover:underline font-medium mt-1">
                            <ExternalLink size={10}/> Track Inbound Shipment
                          </a>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">No inbound courier details added yet</p>
                    )}
                  </div>

                  {/* 3-step inbound timeline */}
                  <div className="relative" data-testid="inbound-status-timeline">
                    <div className="absolute left-5 top-3 bottom-3 w-0.5 bg-slate-100" />
                    <div className="space-y-0">
                      {INBOUND_PIPELINE.map((step, i) => {
                        const inboundIdx = INBOUND_PIPELINE.findIndex(s => s.status === job.inbound_status);
                        const done    = i < inboundIdx;
                        const current = i === inboundIdx;
                        const future  = i > inboundIdx;
                        const Icon    = step.icon;
                        return (
                          <div key={step.status} className={`relative flex gap-4 pb-5 last:pb-0 ${future ? "opacity-40" : ""}`}>
                            <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                              current ? "border-orange-400 bg-orange-50 shadow-md" : done ? "border-orange-300 bg-orange-50" : "border-slate-200 bg-white"
                            }`}>
                              {current
                                ? <div className="relative"><div className="absolute inset-0 rounded-full animate-ping opacity-25" style={{ background: step.dot }}/><Icon size={16} style={{ color: step.dot }} className="relative z-10"/></div>
                                : <Icon size={16} className={done ? "text-orange-600" : "text-slate-300"}/>}
                            </div>
                            <div className="flex-1 min-w-0 pt-1.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-base">{step.emoji}</span>
                                <span className={`text-sm font-semibold ${current ? "text-orange-700" : done ? "text-slate-800" : "text-slate-400"}`}>{step.status}</span>
                                {current && <span className="text-[10px] bg-orange-100 text-orange-700 font-semibold px-2 py-0.5 rounded-full">Current</span>}
                              </div>
                              <p className={`text-xs mt-0.5 ${done || current ? "text-slate-500" : "text-slate-300"}`}>{step.desc}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Advance inbound status button */}
                  {(() => {
                    const inboundIdx = INBOUND_PIPELINE.findIndex(s => s.status === job.inbound_status);
                    const nextStep   = INBOUND_PIPELINE[inboundIdx + 1];
                    return nextStep ? (
                      <Button className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-white text-xs h-8"
                        disabled={saving} onClick={handleAdvanceInboundStatus}>
                        {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/>}
                        {nextStep.emoji} Mark as {nextStep.status} →
                      </Button>
                    ) : (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl text-center">
                        <p className="text-sm font-semibold text-green-700">🎉 Charger Received at Service Center</p>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* ── STANDARD TIMELINE: POST-RECEIVED or LEGACY ── */
                (() => {
                  const postRx = isPostReceived(job.current_status, job.status_history || []);

                  if (postRx) {
                    /* ── POST-RECEIVED 7-STEP PIPELINE ── */
                    const currentPRIdx = getPostReceivedIdx(job.current_status);
                    // Build history lookup: for each stage find first matching history entry
                    const histByStage = {};
                    POST_RECEIVED_PIPELINE.forEach(step => {
                      const aliases = POST_RX_HISTORY[step.status] || [step.status];
                      const entry = (job.status_history || []).find(h => aliases.includes(h.status));
                      if (entry) histByStage[step.status] = entry;
                    });
                    return (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Service Journey</h3>
                          <span className="text-xs text-slate-400">{Math.min(currentPRIdx+1, POST_RECEIVED_PIPELINE.length)} / {POST_RECEIVED_PIPELINE.length} steps</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-5">
                          <div className="h-full bg-green-500 rounded-full transition-all"
                            style={{ width:`${((currentPRIdx+1)/POST_RECEIVED_PIPELINE.length)*100}%` }}/>
                        </div>
                        <div className="relative">
                          <div className="absolute left-5 top-3 bottom-3 w-0.5 bg-slate-100"/>
                          <div className="space-y-0">
                            {POST_RECEIVED_PIPELINE.map((step, i) => {
                              const done    = i < currentPRIdx;
                              const current = i === currentPRIdx;
                              const future  = i > currentPRIdx;
                              const hist    = histByStage[step.status];
                              const Icon    = step.icon;
                              return (
                                <div key={step.status} className={`relative flex gap-4 pb-5 last:pb-0 ${future ? "opacity-40" : ""}`}>
                                  <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                                    current ? "border-green-500 bg-green-50 shadow-md" : done ? "border-green-300 bg-green-50" : "border-slate-200 bg-white"
                                  }`}>
                                    {current
                                      ? <div className="relative"><div className="absolute inset-0 rounded-full animate-ping opacity-25" style={{background:step.dot}}/><Icon size={16} style={{color:step.dot}} className="relative z-10"/></div>
                                      : <Icon size={16} className={done ? "text-green-600" : "text-slate-300"}/>}
                                  </div>
                                  <div className="flex-1 min-w-0 pt-1.5">
                                    <div className="flex items-start justify-between gap-2 flex-wrap">
                                      <div className="flex items-center gap-2">
                                        <span className="text-base">{step.emoji}</span>
                                        <span className={`text-sm font-semibold ${current?"text-green-700":done?"text-slate-800":"text-slate-400"}`}>{step.status}</span>
                                        {current && <span className="text-[10px] bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">Current</span>}
                                      </div>
                                      {hist && (
                                        <span className="text-xs font-mono text-slate-500 bg-slate-50 px-2 py-0.5 rounded-lg border border-slate-100 whitespace-nowrap">
                                          {fmtPostReceived(hist.timestamp)}
                                        </span>
                                      )}
                                    </div>
                                    <p className={`text-xs mt-0.5 ${done||current?"text-slate-500":"text-slate-300"}`}>{step.desc}</p>
                                    {hist?.notes && <p className="text-xs mt-1 text-slate-600 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">📝 {hist.notes}</p>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    );
                  }

                  /* ── LEGACY 9-STEP PIPELINE (pre-received pickup journey) ── */
                  const xpEvents = [];
                  (job.products || []).forEach((p, pi) => {
                    const label = (job.products || []).length > 1 ? ` (Charger #${pi+1})` : "";
                    if (p.xp_charger_status && p.outgoing_courier?.awb_number) {
                      xpEvents.push({
                        key: `out-${pi}`, kind: "new_given",
                        title: `New Charger Dispatched to Customer${label}`,
                        details: [
                          p.outgoing_courier.courier_name && `Courier: ${p.outgoing_courier.courier_name}`,
                          `AWB: ${p.outgoing_courier.awb_number}`,
                          p.outgoing_courier.dispatch_date && `Dispatch Date: ${p.outgoing_courier.dispatch_date}`,
                        ].filter(Boolean),
                        tracking_url: p.outgoing_courier.tracking_url,
                      });
                    }
                    if (p.xp_charger_status === "returned" && p.incoming_courier?.awb_number) {
                      xpEvents.push({
                        key: `in-${pi}`, kind: "returned",
                        title: `Original Charger Dispatched by Customer${label}`,
                        details: [
                          `AWB: ${p.incoming_courier.awb_number}`,
                          p.incoming_courier.expected_return_date && `Expected Return: ${p.incoming_courier.expected_return_date}`,
                        ].filter(Boolean),
                        tracking_url: p.incoming_courier.tracking_url,
                      });
                    }
                  });
                  const IN_DISPATCH_IDX = LEGACY_PIPELINE.findIndex(s => s.status === "In Dispatch");

                  return (
                    <>
                      <div className="flex items-center justify-between mb-5">
                        <h3 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Service Journey Timeline</h3>
                        <span className="text-xs text-slate-400">{currentIdx + 1} / {LEGACY_PIPELINE.length} steps</span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-5">
                        <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${((currentIdx + 1) / LEGACY_PIPELINE.length) * 100}%` }} />
                      </div>
                      <div className="relative" data-testid="job-detail-status-timeline">
                        <div className="absolute left-5 top-3 bottom-3 w-0.5 bg-slate-100" />
                        <div className="space-y-0">
                          {LEGACY_PIPELINE.map((step, i) => {
                            const done    = i < currentIdx;
                            const current = i === currentIdx;
                            const future  = i > currentIdx;
                            const hist    = historyMap[step.status];
                            const Icon    = step.icon;
                            return (
                              <>
                                <div key={step.status} className={`relative flex gap-4 pb-5 ${future ? "opacity-40" : ""}`}>
                                  <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${current ? "border-green-500 bg-green-50 shadow-md" : done ? "border-green-300 bg-green-50" : "border-slate-200 bg-white"}`}>
                                    {current ? <div className="relative"><div className="absolute inset-0 rounded-full animate-ping opacity-25" style={{ background: step.dot }} /><Icon size={16} style={{ color: step.dot }} className="relative z-10" /></div> : <Icon size={16} className={done ? "text-green-600" : "text-slate-300"} />}
                                  </div>
                                  <div className="flex-1 min-w-0 pt-1.5">
                                    <div className="flex items-center justify-between flex-wrap gap-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-base">{step.emoji}</span>
                                        <span className={`text-sm font-semibold ${current ? "text-green-700" : done ? "text-slate-800" : "text-slate-400"}`}>{step.status}</span>
                                        {current && <span className="text-[10px] bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">Current</span>}
                                      </div>
                                      {hist && <span className="text-xs text-slate-400">{fmtDT(hist.timestamp)}</span>}
                                    </div>
                                    <p className={`text-xs mt-0.5 ${done || current ? "text-slate-500" : "text-slate-300"}`}>{step.desc}</p>
                                    {hist?.notes && <p className="text-xs mt-1 text-slate-600 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">📝 {hist.notes}</p>}
                                  </div>
                                </div>
                                {i === IN_DISPATCH_IDX && xpEvents.length > 0 && xpEvents.map(ev => (
                                  <div key={ev.key} className="relative flex gap-4 pb-5">
                                    <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${ev.kind === "new_given" ? "border-green-400 bg-green-50" : "border-orange-400 bg-orange-50"}`}>
                                      <Truck size={16} className={ev.kind === "new_given" ? "text-green-600" : "text-orange-600"} />
                                    </div>
                                    <div className="flex-1 min-w-0 pt-1.5">
                                      <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span className="text-sm">{ev.kind === "new_given" ? "🎁" : "🔄"}</span>
                                        <span className={`text-sm font-semibold ${ev.kind === "new_given" ? "text-green-800" : "text-orange-800"}`}>{ev.title}</span>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ev.kind === "new_given" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>XP</span>
                                      </div>
                                      <div className={`text-xs rounded-lg px-2.5 py-2 space-y-0.5 ${ev.kind === "new_given" ? "bg-green-50 border border-green-200" : "bg-orange-50 border border-orange-200"}`}>
                                        {ev.details.map((d, di) => <p key={di} className={ev.kind === "new_given" ? "text-green-800" : "text-orange-800"}>{d}</p>)}
                                        {ev.tracking_url && <a href={ev.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline font-medium mt-0.5">Track Shipment ↗</a>}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — payment + actions */}
        <div className="space-y-5">
          {/* Payment */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center"><CreditCard size={15} className="text-emerald-600"/></div>
                  <h3 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Payment</h3>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-green-600" onClick={() => { setPayForm({...job.payment}); setEditPayment(true); }}><Edit3 size={13}/></Button>
              </div>
              <div className="space-y-2 mb-4">
                {[["Total",`\u20b9${(job.payment?.amount||0).toLocaleString("en-IN")}`,"text-slate-900"],["Paid",`\u20b9${(job.payment?.paid_amount||0).toLocaleString("en-IN")}`,"text-emerald-700"]].map(([l,v,c])=>(
                  <div key={l} className="flex justify-between"><span className="text-xs text-slate-500">{l}</span><span className={`text-sm font-semibold tabular-nums ${c}`}>{v}</span></div>
                ))}
                <Separator />
                <div className="flex justify-between">
                  <span className="text-xs font-semibold text-slate-600">Due</span>
                  <span className={`text-sm font-bold tabular-nums ${ (job.payment?.due_amount||0) > 0 ? "text-rose-600" : "text-emerald-600" }`} data-testid="job-detail-payment-due-amount">
                    \u20b9{(job.payment?.due_amount||0).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg mb-2">
                <span className="text-xs text-slate-500">Method</span>
                <span className="text-xs font-medium">{job.payment?.method||"Cash"}</span>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${PAY_STYLES[job.payment?.payment_status]||"bg-slate-100"}`} data-testid="payment-status-chip">
                {job.payment?.payment_status||"Unpaid"}
              </span>
              {job.payment?.payment_status !== "Paid" && job.payment?.amount > 0 && (
                <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                  onClick={() => { setPayForm({...job.payment, paid_amount: job.payment?.amount||0}); setEditPayment(true); }}
                  data-testid="job-detail-mark-paid-button">Mark as Fully Paid</Button>
              )}
            </CardContent>
          </Card>

          {/* Courier Details */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center"><Truck size={15} className="text-blue-600"/></div>
                  <h3 className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Courier Details</h3>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-green-700 hover:bg-green-50"
                  onClick={() => { setCourierForm({ courier_name: job.courier_details?.courier_name||"", awb_number: job.courier_details?.awb_number||"", tracking_url: job.courier_details?.tracking_url||"" }); setEditCourier(true); }}>
                  <Edit3 size={12} className="mr-1"/> {job.courier_details?.awb_number ? "Edit" : "Add"}
                </Button>
              </div>
              {job.courier_details?.awb_number ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">Courier</span>
                    <span className="text-xs font-semibold text-slate-700">{job.courier_details.courier_name || "—"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">AWB Number</span>
                    <span className="text-xs font-mono font-bold text-blue-700">{job.courier_details.awb_number}</span>
                  </div>
                  {job.courier_details.tracking_url && (
                    <a href={job.courier_details.tracking_url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700 font-medium mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
                      <ExternalLink size={12}/> Track Shipment
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No courier details added yet</p>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Quick Actions</h3>
              <div className="space-y-2">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                  onClick={() => { setStatusForm({ status: job.current_status, notes: "" }); setUpdateStatus(true); }}>
                  <CheckCircle size={13} className="mr-1.5"/>Update Status
                </Button>
                <Button variant="outline" className="w-full text-xs h-8 border-purple-200 text-purple-700 hover:bg-purple-50"
                  onClick={() => setAssignTech(true)}>
                  👷 {job.technician ? `Reassign (${job.technician})` : "Assign Technician"}
                </Button>
                <a href={`/print/${job.id}`} target="_blank" rel="noreferrer" className="block">
                  <Button variant="outline" className="w-full text-xs h-8"><Printer size={13} className="mr-1.5"/>Print / Invoice</Button>
                </a>
                <a href={trackingUrl} target="_blank" rel="noreferrer" className="block">
                  <Button variant="outline" className="w-full text-xs h-8 border-green-200 text-green-700 hover:bg-green-50">Share Tracking Link</Button>
                </a>
                <Link to="/jobs/new" className="block">
                  <Button variant="outline" className="w-full text-xs h-8">New Job</Button>
                </Link>
                {/* Create SC Job — pre-fills /sc/new with this job's details */}
                <Link
                  to="/sc/new"
                  state={{ prefill: {
                    from_job_no: job.job_no,
                    customer: {
                      name:      job.customer?.name      || "",
                      phone:     job.customer?.phone     || "",
                      alt_phone: job.customer?.alt_phone || "",
                      address:   job.customer?.address   || "",
                      landmark:  job.customer?.landmark  || "",
                      city:      job.customer?.city      || "",
                      state:     job.customer?.state     || "",
                      pincode:   job.customer?.pincode   || "",
                    },
                    chargers: (job.products || []).map(p => {
                      const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
                      const parts = (p.purchase_date || "").split(" ");
                      const monthIdx = MONTH_NAMES.indexOf(parts[0]);
                      return {
                        charger_type:    p.charger_type    || "",
                        charger_spec:    p.charger_spec    || "",
                        serial_number:   p.serial_number   || "",
                        body_type:       p.body_type       || "",
                        warranty_status: "",
                        mfg_month:       monthIdx >= 0 ? String(monthIdx + 1) : "",
                        mfg_year:        parts[1]          || "",
                      };
                    }),
                  }}}
                  className="block"
                  data-testid="job-detail-create-sc-btn">
                  <Button variant="outline" className="w-full text-xs h-8 border-green-200 text-green-700 hover:bg-green-50 font-semibold">
                    <Wrench size={12} className="mr-1.5"/> Create SC Job from this Job
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* DIALOGS */}
      <Dialog open={editCustomer} onOpenChange={setEditCustomer}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{fontFamily:"'Space Grotesk',sans-serif"}}>Edit Customer Details</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full Name</Label><Input className="mt-1.5 h-9 text-sm" value={custForm.name||""} onChange={e=>setCustForm({...custForm,name:e.target.value})}/></div>
              <div><Label className="text-xs">Phone</Label><Input className="mt-1.5 h-9 text-sm" value={custForm.phone||""} onChange={e=>setCustForm({...custForm,phone:e.target.value})}/></div>
            </div>
            <div><Label className="text-xs">Alternate Phone</Label><Input className="mt-1.5 h-9 text-sm" placeholder="Alternative number" value={custForm.alt_phone||""} onChange={e=>setCustForm({...custForm,alt_phone:e.target.value})}/></div>
            <div><Label className="text-xs">Street Address</Label><Textarea className="mt-1.5 text-sm" rows={2} value={custForm.address||""} onChange={e=>setCustForm({...custForm,address:e.target.value})}/></div>
            <div><Label className="text-xs">Landmark</Label><Input className="mt-1.5 h-9 text-sm" placeholder="Near Metro Station..." value={custForm.landmark||""} onChange={e=>setCustForm({...custForm,landmark:e.target.value})}/></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">City</Label><Input className="mt-1.5 h-9 text-sm" value={custForm.city||""} onChange={e=>setCustForm({...custForm,city:e.target.value})}/></div>
              <div>
                <Label className="text-xs">State</Label>
                <Select value={custForm.state||""} onValueChange={v=>setCustForm({...custForm,state:v})}>
                  <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue placeholder="State"/></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {INDIA_STATES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">PIN Code</Label><Input className="mt-1.5 h-9 text-sm font-mono" maxLength={6} value={custForm.pincode||""} onChange={e=>setCustForm({...custForm,pincode:e.target.value})}/></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={()=>setEditCustomer(false)}>Cancel</Button><Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={saving} onClick={()=>save("customer",custForm).then(()=>setEditCustomer(false))}><Spin/>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editProduct} onOpenChange={setEditProduct}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"'Space Grotesk',sans-serif"}}>
              {prodEditIdx === null ? "Add Charger" : `Edit Charger #${(prodEditIdx||0)+1}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">

            {/* Charger Type Segments */}
            <div>
              <Label className="text-xs font-medium text-slate-700">Charger Type</Label>
              <div className="flex gap-2 mt-1.5">
                {CHARGER_SEGMENTS.map(seg => (
                  <button key={seg.label} type="button"
                    onClick={() => setProdForm(f => ({ ...f, charger_type: seg.label, charger_spec: "" }))}
                    className={`flex-1 py-2 px-1 rounded-xl text-xs font-semibold border transition-all leading-tight ${
                      prodForm.charger_type === seg.label
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-green-400 hover:text-green-700"
                    }`}>
                    <span className="block text-base mb-0.5">{seg.icon}</span>{seg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Model dropdown */}
            {prodForm.charger_type && (
              <div>
                <Label className="text-xs font-medium text-slate-700">Model <span className="text-rose-500">*</span></Label>
                <Select value={prodForm.charger_spec||""} onValueChange={v=>setProdForm(f=>({...f,charger_spec:v}))}>
                  <SelectTrigger className="mt-1.5 h-9 text-sm font-semibold">
                    <SelectValue placeholder={`Select ${prodForm.charger_type} model…`}/>
                  </SelectTrigger>
                  <SelectContent>
                    {(SEGMENT_MODELS[prodForm.charger_type]||[]).map(m=>(
                      <SelectItem key={m} value={m}><span className="font-mono font-semibold text-green-700">{m}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Legacy fallback: if charger_spec set but charger_type not selected */}
                {!prodForm.charger_type && prodForm.charger_spec && (
                  <p className="text-xs text-slate-500 mt-1">Current: {prodForm.charger_spec}</p>
                )}
              </div>
            )}
            {!prodForm.charger_type && (
              <div>
                <Label className="text-xs font-medium text-slate-700">Battery Cut-off / Ampere <span className="text-rose-500">*</span></Label>
                <Select value={prodForm.charger_spec||""} onValueChange={v=>setProdForm({...prodForm,charger_spec:v})}>
                  <SelectTrigger className="mt-1.5 h-9 text-sm font-semibold"><SelectValue placeholder="Select voltage / ampere"/></SelectTrigger>
                  <SelectContent>
                    {CHARGER_SPECS.map(s=>(<SelectItem key={s} value={s}><span className="font-mono font-semibold text-green-700">{s}</span></SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs font-medium text-slate-700">Serial Number</Label>
              <Input className="mt-1.5 h-9 text-sm font-mono" placeholder="SN-XXXXXX (optional)" value={prodForm.serial_number||""} onChange={e=>setProdForm({...prodForm,serial_number:e.target.value})}/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-700">Purchase Month</Label>
                <Select value={prodForm.purchase_month||""} onValueChange={v=>setProdForm({...prodForm,purchase_month:v})}>
                  <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue placeholder="Month"/></SelectTrigger>
                  <SelectContent className="max-h-52">
                    {MONTHS.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Purchase Year</Label>
                <Select value={prodForm.purchase_year||""} onValueChange={v=>setProdForm({...prodForm,purchase_year:v})}>
                  <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue placeholder="Year"/></SelectTrigger>
                  <SelectContent className="max-h-52">{YEARS.map(y=><SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Body Type</Label>
              <Select value={prodForm.body_type||""} onValueChange={v=>setProdForm({...prodForm,body_type:v})}>
                <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue placeholder="Select body type"/></SelectTrigger>
                <SelectContent>{BODY_TYPES.map(b=><SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* ── XP Charger Section ──────────────────────────────────── */}
            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <Label className="text-xs font-bold text-slate-600 uppercase tracking-wide">XP Charger</Label>
                <span className="text-[10px] text-slate-400">(warranty replacement)</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button type="button"
                  onClick={() => setProdForm(f => ({ ...f, xp_charger_status: f.xp_charger_status === "returned" ? "" : "returned" }))}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                    prodForm.xp_charger_status === "returned"
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-orange-50 text-orange-700 border-orange-200 hover:border-orange-400"
                  }`}>
                  <RotateCcw size={13}/> Returned to Service Centre
                </button>
                {isEligibleForNew(prodForm.purchase_month, prodForm.purchase_year) ? (
                  <button type="button"
                    onClick={() => setProdForm(f => ({ ...f, xp_charger_status: f.xp_charger_status === "new_given" ? "" : "new_given" }))}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                      prodForm.xp_charger_status === "new_given"
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-green-50 text-green-700 border-green-200 hover:border-green-400"
                    }`}>
                    <Gift size={13}/> New Given to Customer
                  </button>
                ) : (prodForm.purchase_month && prodForm.purchase_year) ? (
                  <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-slate-500 bg-slate-100 border-2 border-slate-200">
                    <Info size={12}/> Not eligible — warranty period expired
                  </div>
                ) : (
                  <div className="text-xs text-slate-400 flex items-center gap-1.5 px-3 py-2">
                    <Info size={11}/> Set purchase date to check eligibility
                  </div>
                )}
              </div>

              {/* Courier fields — shown only when XP selected */}
              {prodForm.xp_charger_status && (
                <div className="mt-3 space-y-3">
                  {/* Outgoing */}
                  <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Truck size={12} className="text-green-700"/>
                      <p className="text-[10px] font-bold text-green-800 uppercase tracking-wide">New Charger Dispatched to Customer</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[["courier_name","Courier Name"],["awb_number","AWB Number"],["tracking_url","Tracking URL"],["dispatch_date","Dispatch Date"]].map(([k,l]) => (
                        <div key={k} className={k === "tracking_url" ? "col-span-2" : ""}>
                          <Label className="text-[10px] text-slate-600">{l}</Label>
                          <Input className="mt-0.5 h-8 text-xs" type={k === "dispatch_date" ? "date" : "text"}
                            placeholder={k === "awb_number" ? "AWB / tracking no." : ""}
                            value={prodForm.outgoing_courier?.[k]||""}
                            onChange={e => setProdForm(f => ({ ...f, outgoing_courier: { ...(f.outgoing_courier||{}), [k]: e.target.value } }))}/>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Incoming */}
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Truck size={12} className="text-orange-700"/>
                      <p className="text-[10px] font-bold text-orange-800 uppercase tracking-wide">Original Charger Return from Customer</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[["courier_name","Courier Name"],["awb_number","AWB Number"],["tracking_url","Tracking URL"],["expected_return_date","Expected Return Date"]].map(([k,l]) => (
                        <div key={k} className={k === "tracking_url" ? "col-span-2" : ""}>
                          <Label className="text-[10px] text-slate-600">{l}</Label>
                          <Input className="mt-0.5 h-8 text-xs" type={k === "expected_return_date" ? "date" : "text"}
                            placeholder={k === "awb_number" ? "AWB / tracking no." : ""}
                            value={prodForm.incoming_courier?.[k]||""}
                            onChange={e => setProdForm(f => ({ ...f, incoming_courier: { ...(f.incoming_courier||{}), [k]: e.target.value } }))}/>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={()=>setEditProduct(false)}>Cancel</Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
              disabled={saving||!prodForm.charger_spec}
              onClick={() => {
                const purchaseDate = [prodForm.purchase_month, prodForm.purchase_year].filter(Boolean).join(" ");
                saveProduct({
                  charger_type: prodForm.charger_type||"",
                  charger_spec: prodForm.charger_spec,
                  serial_number: prodForm.serial_number||"",
                  purchase_date: purchaseDate,
                  body_type: prodForm.body_type,
                  xp_charger_status: prodForm.xp_charger_status||"",
                  outgoing_courier: prodForm.xp_charger_status ? (prodForm.outgoing_courier||{}) : null,
                  incoming_courier: prodForm.xp_charger_status ? (prodForm.incoming_courier||{}) : null,
                });
              }}>
              <Spin/>{prodEditIdx===null?"Add Charger":"Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editSpecs} onOpenChange={setEditSpecs}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle style={{fontFamily:"'Space Grotesk',sans-serif"}}>Edit Fault & Notes</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Fault Description</Label><Textarea className="mt-1.5 text-sm" rows={3} value={specsForm.fault_description||""} onChange={e=>setSpecsForm({...specsForm,fault_description:e.target.value})}/></div>
            <div><Label className="text-xs">Technical Notes</Label><Textarea className="mt-1.5 text-sm" rows={2} value={specsForm.technical_notes||""} onChange={e=>setSpecsForm({...specsForm,technical_notes:e.target.value})}/></div>
            <div><Label className="text-xs">Repair Notes</Label><Textarea className="mt-1.5 text-sm" rows={2} value={specsForm.repair_notes||""} onChange={e=>setSpecsForm({...specsForm,repair_notes:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={()=>setEditSpecs(false)}>Cancel</Button><Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={saving} onClick={()=>save("specs",specsForm).then(()=>setEditSpecs(false))}><Spin/>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editPayment} onOpenChange={setEditPayment}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle style={{fontFamily:"'Space Grotesk',sans-serif"}}>Edit Payment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Total (&#8377;)</Label><Input type="number" min="0" className="mt-1.5 h-9 text-sm" value={payForm.amount??""} onChange={e=>setPayForm({...payForm,amount:parseFloat(e.target.value)||0})}/></div>
            <div><Label className="text-xs">Paid (&#8377;)</Label><Input type="number" min="0" className="mt-1.5 h-9 text-sm" value={payForm.paid_amount??""} onChange={e=>setPayForm({...payForm,paid_amount:parseFloat(e.target.value)||0})}/></div>
            <div><Label className="text-xs">Method</Label>
              <Select value={payForm.method||"Cash"} onValueChange={v=>setPayForm({...payForm,method:v})}>
                <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="Card">Card</SelectItem><SelectItem value="Bank Transfer">Bank Transfer</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={()=>setEditPayment(false)}>Cancel</Button><Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={saving} onClick={()=>save("payment",payForm).then(()=>setEditPayment(false))}><Spin/>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateStatus} onOpenChange={setUpdateStatus}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle style={{fontFamily:"'Space Grotesk',sans-serif"}}>Update Job Status</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">New Status</Label>
              <Select value={statusForm.status} onValueChange={v=>setStatusForm({...statusForm,status:v})}>
                <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue/></SelectTrigger>
                <SelectContent className="max-h-64">
                  {/* Deduplicate: "In Transit" appears at steps 5 & 9 — show each unique status once with its description */}
                  {LEGACY_PIPELINE.filter((step, i, arr) => arr.findIndex(s => s.status === step.status) === i).map(step => (
                    <SelectItem key={step.status} value={step.status}>
                      <span className="flex items-center gap-2">
                        <span>{step.emoji}</span>
                        <span>{step.status}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Notes (optional)</Label><Textarea className="mt-1.5 text-sm" rows={2} placeholder="Add a note..." value={statusForm.notes} onChange={e=>setStatusForm({...statusForm,notes:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={()=>setUpdateStatus(false)}>Cancel</Button><Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={saving||!statusForm.status} onClick={saveStatus}><Spin/>Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this job?</AlertDialogTitle><AlertDialogDescription>This will permanently delete <strong>{job.job_no}</strong>.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={deleteJob}>Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Inbound Courier Dialog */}
      <Dialog open={editInbound} onOpenChange={setEditInbound}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"'Space Grotesk',sans-serif"}}>
              <div className="flex items-center gap-2"><Truck size={16} className="text-orange-500"/> Customer's Inbound Courier</div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Courier Company</Label>
              <Input placeholder="e.g. Ekart, Amazon, Delhivery" value={inboundCourierForm.courier_name}
                onChange={e => setInboundCourierForm(f => ({ ...f, courier_name: e.target.value }))}
                className="mt-1.5 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">AWB / Tracking Number <span className="text-rose-500">*</span></Label>
              <Input placeholder="e.g. 1234567890123" value={inboundCourierForm.awb_number}
                onChange={e => setInboundCourierForm(f => ({ ...f, awb_number: e.target.value }))}
                className="mt-1.5 h-9 text-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Tracking Link (optional)</Label>
              <Input placeholder="https://track.courier.com/..." value={inboundCourierForm.tracking_url}
                onChange={e => setInboundCourierForm(f => ({ ...f, tracking_url: e.target.value }))}
                className="mt-1.5 h-9 text-sm" />
              {inboundCourierForm.tracking_url && (
                <a href={inboundCourierForm.tracking_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1">
                  <ExternalLink size={10}/> Preview link
                </a>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditInbound(false)}>Cancel</Button>
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
              disabled={saving || !inboundCourierForm.awb_number.trim()}
              onClick={handleSaveInboundCourier}>
              <Spin/>Save Inbound Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Courier Dialog */}
      <Dialog open={editCourier} onOpenChange={setEditCourier}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"'Space Grotesk',sans-serif"}}>
              <div className="flex items-center gap-2"><Truck size={16} className="text-blue-600"/>
                {job.courier_details?.awb_number ? "Edit Courier Details" : "Add Courier Details"}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Courier Company</Label>
              <Select value={courierForm.courier_name}
                onValueChange={v => {
                  const autoUrl = buildTrackingUrl(v, courierForm.awb_number);
                  setCourierForm({ ...courierForm, courier_name: v, tracking_url: autoUrl || courierForm.tracking_url });
                }}>
                <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue placeholder="Select courier service"/></SelectTrigger>
                <SelectContent>
                  {COURIER_SERVICES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">AWB / Tracking Number <span className="text-rose-500">*</span></Label>
              <Input
                placeholder="e.g. 1234567890123"
                value={courierForm.awb_number}
                onChange={e => {
                  const awb = e.target.value;
                  const autoUrl = buildTrackingUrl(courierForm.courier_name, awb);
                  setCourierForm({ ...courierForm, awb_number: awb, tracking_url: autoUrl || courierForm.tracking_url });
                }}
                className="mt-1.5 h-9 text-sm font-mono"
                data-testid="courier-awb-input"
              />
            </div>
            {/* Tracking URL — always editable, auto-populated for known couriers */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-medium text-slate-700">Tracking Link</Label>
                {buildTrackingUrl(courierForm.courier_name, courierForm.awb_number) && (
                  <button type="button"
                    className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                    onClick={() => setCourierForm({ ...courierForm, tracking_url: buildTrackingUrl(courierForm.courier_name, courierForm.awb_number) })}>
                    Auto-fill
                  </button>
                )}
              </div>
              <Input
                placeholder="https://courier.com/track/... (optional)"
                value={courierForm.tracking_url}
                onChange={e => setCourierForm({ ...courierForm, tracking_url: e.target.value })}
                className="h-9 text-sm"
                data-testid="courier-tracking-url-input"
              />
              {courierForm.tracking_url && (
                <a href={courierForm.tracking_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1">
                  <ExternalLink size={10}/> Preview link
                </a>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditCourier(false)}>Cancel</Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
              disabled={saving || !courierForm.awb_number.trim()}
              onClick={saveCourier}
              data-testid="courier-save-button">
              <Spin/>Save Courier Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Assign Technician Dialog */}
      <Dialog open={assignTech} onOpenChange={setAssignTech}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{fontFamily:"'Space Grotesk',sans-serif"}}>👷 Assign Technician</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {technicians.length === 0 ? (
              <p className="text-sm text-slate-500">No technicians added yet. Add them in <a href="/settings" className="text-green-600 hover:underline">Settings</a>.</p>
            ) : (
              <div className="space-y-2">
                <button
                  className={`w-full text-left p-3 rounded-xl border text-sm transition-colors ${!job.technician ? "border-green-500 bg-green-50 text-green-700 font-medium" : "border-slate-200 hover:border-slate-300"}`}
                  onClick={() => { save("technician", "").then(() => setAssignTech(false)); }}>
                  — Unassigned
                </button>
                {technicians.map(t => (
                  <button key={t}
                    className={`w-full text-left p-3 rounded-xl border text-sm transition-colors flex items-center gap-2 ${job.technician === t ? "border-purple-500 bg-purple-50 text-purple-700 font-semibold" : "border-slate-200 hover:border-slate-300"}`}
                    onClick={() => { save("technician", t).then(() => setAssignTech(false)); }}>
                    <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-700 text-xs font-bold">{t.charAt(0).toUpperCase()}</span>
                    </div>
                    {t}
                    {job.technician === t && <span className="ml-auto text-xs text-purple-500">Current</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAssignTech(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
